import React, { useState } from "react";
import { HiPlay, HiX } from "react-icons/hi";

const Hero = () => {
  const [isOpen, setOpen] = useState(false);

  return (
    <section
      id="home"
      className="relative min-h-screen flex items-center
                 bg-gradient-to-b from-primary-600 via-primary-500 to-primary-400
                 lg:bg-gradient-to-r lg:from-primary-600 lg:via-primary-300 lg:to-white
                 pt-16 lg:pt-20 pb-10 lg:pb-0 overflow-hidden"
    >
      {/* Background Pattern Layer */}
      <div className="absolute inset-0 hero-grid-pattern-dark pointer-events-none" />

      {/* Decorative Gradient Blobs */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-24 -right-24 w-96 h-96 bg-white/20 rounded-full blur-3xl" />
        <div className="absolute -bottom-32 -left-32 w-80 h-80 bg-accent-500/30 rounded-full blur-3xl" />
        <div className="absolute top-1/2 right-1/4 w-64 h-64 bg-white/15 rounded-full blur-2xl" />
      </div>

      {/* Floating Geometric Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Animated dots */}
        <div className="absolute top-1/4 left-1/4 w-4 h-4 bg-white/40 rounded-full animate-float" />
        <div
          className="absolute top-1/3 right-1/3 w-3 h-3 bg-accent-400/50 rounded-full animate-float-slow"
          style={{ animationDelay: "1s" }}
        />
        <div
          className="absolute bottom-1/3 left-[20%] w-5 h-5 bg-white/35 rounded-full animate-float"
          style={{ animationDelay: "2s" }}
        />

        {/* Hexagon decoration - top right */}
        <svg
          className="absolute top-24 right-8 lg:right-16 w-32 lg:w-48 h-32 lg:h-48 text-white/30 animate-float-slow"
          viewBox="0 0 100 100"
          fill="none"
        >
          <polygon
            points="50,5 93,25 93,75 50,95 7,75 7,25"
            stroke="currentColor"
            strokeWidth="1"
          />
        </svg>

        {/* Hexagon decoration - bottom left */}
        <svg
          className="absolute bottom-24 left-8 lg:left-16 w-24 lg:w-32 h-24 lg:h-32 text-white/25 animate-float"
          viewBox="0 0 100 100"
          fill="none"
          style={{ animationDelay: "3s" }}
        >
          <polygon
            points="50,10 85,30 85,70 50,90 15,70 15,30"
            stroke="currentColor"
            strokeWidth="1.5"
          />
        </svg>
      </div>

      {/* Main Content */}
      <div className="w-full relative z-10">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-0 items-center">
          {/* Content Column */}
          <div className="order-2 lg:order-1 px-4 lg:pl-8 xl:pl-16 2xl:pl-24">
            {/* Badge */}
            <div className="opacity-0 animate-slide-in-left animate-stagger-1">
              <span
                className="inline-block px-4 py-1.5 bg-white/20 backdrop-blur-sm
                             text-white text-sm font-medium rounded-full mb-4"
              >
                Electronics & Software Solutions
              </span>
            </div>

            <h1
              className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight
                         opacity-0 animate-slide-in-left animate-stagger-2"
            >
              HexaPi Tech
            </h1>

            <p
              className="text-base md:text-lg text-primary-100 mb-8 leading-relaxed max-w-lg
                        opacity-0 animate-slide-in-left animate-stagger-3"
            >
              We develop innovative and customized electronic and software
              solutions. From prototyping to deployment, HexaPi Tech is the only
              partner you will need.
            </p>

            <div className="flex flex-wrap gap-4 items-center opacity-0 animate-slide-in-left animate-stagger-4">
              <a
                href="#contact"
                className="inline-flex items-center justify-center px-8 py-4
                           bg-white text-primary-600 font-semibold rounded-lg
                           hover:bg-primary-50 transition-all duration-300
                           shadow-lg hover:shadow-xl hover:-translate-y-0.5"
              >
                Get in Touch
              </a>

              {/* Play Button */}
              <button
                onClick={() => setOpen(true)}
                className="flex w-12 h-12 lg:w-14 lg:h-14 bg-white/20 backdrop-blur-sm border-2 border-white/50 rounded-full
                           items-center justify-center hover:bg-white/30
                           hover:scale-110 transition-all duration-300 group relative"
                aria-label="Play video"
              >
                <span className="absolute inset-0 rounded-full bg-white/20 animate-ping-slow opacity-50" />
                <HiPlay className="w-5 h-5 lg:w-6 lg:h-6 text-white relative z-10" />
              </button>
            </div>
          </div>

          {/* Hero Image Column */}
          <div className="order-1 lg:order-2 lg:absolute lg:right-0 lg:top-0 lg:bottom-0 lg:w-1/2
                          relative pl-4 pr-0 lg:pl-0 opacity-0 animate-slide-in-right animate-stagger-2
                          flex items-center">
            <div className="relative lg:ml-8 lg:pt-0">
              {/* Image glow effect */}
              <div className="absolute inset-8 bg-gradient-to-br from-primary-400/20 to-accent-400/15 blur-3xl rounded-full" />

              <img
                src="/images/hero/hero-img.png"
                alt="HexaPi Tech Innovation"
                className="relative w-full max-w-md mx-auto lg:max-w-none lg:ml-auto lg:h-auto drop-shadow-2xl"
              />

              {/* Glassmorphism floating badge */}
              <div
                className="absolute -bottom-4 left-4 lg:bottom-8 lg:left-0
                            bg-white/80 backdrop-blur-md rounded-xl p-4 shadow-lg
                            border border-primary-100 hidden md:flex items-center gap-3
                            opacity-0 animate-slide-up animate-stagger-5"
              >
                <div
                  className="w-12 h-12 bg-gradient-to-br from-primary-500 to-primary-600 rounded-lg
                              flex items-center justify-center shadow-lg"
                >
                  <span className="text-white font-bold text-lg">6+</span>
                </div>
                <div>
                  <p className="text-sm font-semibold text-neutral-dark">
                    Years Experience
                  </p>
                  <p className="text-xs text-neutral">Trusted Partner</p>
                </div>
              </div>

            </div>
          </div>
        </div>
      </div>

      {/* Video Modal */}
      {isOpen && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/90 backdrop-blur-sm"
          onClick={() => setOpen(false)}
        >
          <button
            onClick={() => setOpen(false)}
            className="absolute top-4 right-4 p-2 text-white hover:text-gray-300 transition-colors"
            aria-label="Close video"
          >
            <HiX className="w-8 h-8" />
          </button>
          <div
            className="relative w-full max-w-4xl aspect-video animate-slide-up"
            onClick={(e) => e.stopPropagation()}
          >
            <iframe
              src="https://www.youtube.com/embed/-lbEOuessUs?autoplay=1"
              title="HexaPi Tech Video"
              className="w-full h-full rounded-lg shadow-2xl"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
        </div>
      )}
    </section>
  );
};

export default Hero;
